import c from 'classnames';
import { useLocation, useNavigate } from 'react-router-dom';

import { Card, Icon, TextLink, Typography } from 'components';
import useCurrentKitAndTinyAccount from 'hooks/useCurrentKitAndTinyAccount';

import styles from './ActionsCard.module.scss';

import { IconName } from '@repo/ui';

export type ActionItem = {
  id: string | number;
  action_plan_recommendations_id?: string;
  display_title: string;
  category?: string;
};

type ActionsCardProps = {
  title: string;
  actions: ActionItem[];
  emptyState?: React.ReactNode;
  className?: string;
  children?: React.ReactNode;
  renderItem?: (action: ActionItem, index: number) => React.ReactNode;
};

const getIconForAction = (action: ActionItem): IconName => {
  switch (action.category) {
    case 'SupplementSuggestions':
      return 'pill';
    case 'DietarySuggestions':
      return 'carrot';
    case 'LifeStyleSuggestions':
      return 'heartHealth';
    default:
      return 'heartHealth';
  }
};

const MAX_ACTIONS_TO_DISPLAY = 5;

export const ActionsCard = ({
  title,
  actions,
  className,
  emptyState,
  children,
  renderItem,
}: ActionsCardProps) => {
  const { currentKitId } = useCurrentKitAndTinyAccount();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const handleActionClick = (action: ActionItem) => {
    if (!currentKitId) return;
    navigate(
      `/action-plan/${currentKitId}/action/${action.action_plan_recommendations_id || action.id}`,
      { state: { from: pathname } },
    );
  };

  const defaultEmptyState = (
    <Typography variant='body-s' className={styles.emptyCardText}>
      No actions available
    </Typography>
  );

  return (
    <Card
      variant='wrapper'
      className={c(styles.card, 'purple-border-left-slim', 'p-4', className)}
    >
      <Typography variant='heading-m' className={c(styles.cardTitle, 'mb-2')}>
        {title}
      </Typography>
      <div className={styles.cardContentContainer}>
        {!actions || !actions.length
          ? emptyState || defaultEmptyState
          : actions.slice(0, MAX_ACTIONS_TO_DISPLAY).map((action, index) => {
              if (!action) return null;
              if (renderItem) {
                return renderItem(action, index);
              }
              return (
                <div
                  key={index}
                  className={styles.cardItem}
                  onClick={() => handleActionClick(action)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      handleActionClick(action);
                    }
                  }}
                  role='button'
                  tabIndex={0}
                >
                  <Icon
                    name={getIconForAction(action)}
                    size='m'
                    className={c(styles.cardIcon, 'mr-2')}
                  />
                  <Typography variant='heading-s'>
                    {action.display_title}
                  </Typography>
                  <TextLink
                    label={''}
                    iconNameRight='chevronForward'
                    className={c(
                      styles.cardIconForward,
                      'my-auto ml-auto gap-0',
                    )}
                    contentOnly
                  />
                </div>
              );
            })}
        {children}
      </div>
    </Card>
  );
};
